const retry = require('retry');
const slug = require('slug');
const stringify = require('json-stable-stringify');

const eventPrefix = 'event:';

function debug(msg) {
  // eslint-disable-next-line no-console
  console.debug(`vmol-socketio-gateway-client: ${msg}`);
}

function eventToSlug(name, filters = null) {
  if (filters === null) {
    return name;
  }

  return `${name}:${slug(stringify(filters))}`;
}

class IOApi {
  constructor(io, opts = {}) {
    this.io = io;
    this.subscriptions = [];

    this.connectPromise = null;

    io.on('connect', () => {
      this.onIOConnect();
    });

    this.retryOptions = Object.assign({
      forever: true,
      minTimeout: 20, // 20 ms
      maxTimeout: 15 * 1000, // 15 s
      randomize: true,
    }, opts.retryOptions || {});

    if (typeof opts.retryCallback === 'function') {
      this.retryCallback = opts.retryCallback;
    } else {
      this.retryCallback = () => true;
    }

    this.reconnectOnDisconnect = opts.reconnectOnDisconnect === true;
    this.reconnectOnError = opts.reconnectOnError === true;
    this.debug = opts.debug === true;

    this.io.on('disconnect', (reason) => {
      debug(`Disconnected: ${reason}`);

      // Reconnect on remote server disconnection
      if (reason === 'io server disconnect' && this.reconnectOnDisconnect) {
        this.connect();
      }
    });
  }

  connect() {
    if (this.connectPromise === null) {
      const operation = retry.operation(this.retryOptions);

      this.connectPromise = new Promise((resolve, reject) => {
        const cleanup = () => {
          // eslint-disable-next-line no-use-before-define
          this.io.off('connect', onConnect);
          // eslint-disable-next-line no-use-before-define
          this.io.off('connect_error', onConnectError);

          this.connectPromise = null;
        };

        const onConnect = () => {
          debug('Connection successful');

          operation.retry();
          cleanup();
          return resolve();
        };

        const onConnectError = (err) => {
          debug(`Connection error: ${err}`);

          // Some errors might not close the socket
          this.io.disconnect();

          // User provided callback might cancel the next try
          if (!this.retryCallback(err)) {
            operation.stop();
            cleanup();
            return reject(err);
          }

          // No more tries available
          if (!operation.retry(err)) {
            cleanup();
            return reject(err);
          }

          return null;
        };

        this.io.on('connect', onConnect);
        this.io.on('connect_error', onConnectError);

        operation.attempt((triesCount) => {
          debug(`Connection attempt #${triesCount}`);
          this.io.connect();
        });
      });
    }

    return this.connectPromise;
  }

  onIOConnect() {
    debug(`Socket (re)connected. ${this.subscriptions.length} event subscriptions pending.`);
    this.subscriptions.forEach(e => this.subscribeToEvent(e.name, e.filters));
  }

  subscribeToEvent(name, filters = null) {
    return new Promise((resolve, reject) => {
      this.io.emit('subscribe', { name, filters }, (res) => {
        if (res.success) {
          debug(`Subscribed to event ${name} with filters ${JSON.stringify(filters)}`);
          resolve();
        } else {
          // eslint-disable-next-line no-console
          console.error(`Error while subscribing to event "${name}"`, res.error);
          reject(res.error || null);
        }
      });
    });
  }

  unsubscribeFromEvent(name, filters = null) {
    return new Promise((resolve) => {
      this.io.emit('unsubscribe', { name, filters }, resolve);
    });
  }

  call(actionName, params = {}, opts = {}) {
    return new Promise((resolve, reject) => {
      this.io.emit('call', { actionName, params, opts }, (res) => {
        if (res.success) {
          resolve(res.result || null);
        } else {
          console.error(`Failed to call action ${actionName}`, res.error);
          reject(res.error || null);
        }
      });
    });
  }

  on(name, filters, callback) {
    if (typeof filters === 'function' && callback === undefined) {
      // eslint-disable-next-line no-param-reassign
      callback = filters;
      // eslint-disable-next-line no-param-reassign
      filters = null;
    }

    const eventSlug = eventToSlug(name, filters);

    if (this.subscriptions.find(e => e.slug === eventSlug) !== undefined) {
      this.io.on(eventPrefix + eventSlug, callback);
      return Promise.resolve();
    }

    return this.subscribeToEvent(name, filters)
      .then(() => {
        this.subscriptions.push({ name, filters, slug: eventSlug });
        this.io.on(eventPrefix + eventSlug, callback);
      });
  }

  off(name, filters = null) {
    const eventSlug = eventToSlug(name, filters);

    return this.unsubscribeFromEvent(name, filters)
      .then(() => {
        this.subscriptions = this.subscriptions.filter(e => e.slug !== eventSlug);
        this.io.off(eventPrefix + eventSlug);
      });
  }
}

export default IOApi;
